import React from 'react';

import { validate } from '@vlabs/shared';
import { FaceSample } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Input } from '@vlabs/uikit/components/controls/input/Input';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import type { TOption } from '@vlabs/uikit/types';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetLists } from '@vlabs/api/luna-api/client';

import { AttachmentsInput } from './AttachmentsInput';
import { CardFaceInput } from './CardFaceInput';
import st from './shared.module.sass';
import type { TCreateCardFormData } from '../types';

interface CreateCardFormProps {
  onSubmit: SubmitHandler<TCreateCardFormData>;
  onCancel: VoidFunction;
  defaultValues?: Partial<TCreateCardFormData>;
}

export function CreateCardForm({
  onSubmit,
  onCancel,
  defaultValues = undefined,
}: CreateCardFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const getLists = useGetLists({ page_size: 1000 });
  const listOptions: TOption[] = getLists.data?.lists?.map((list) => ({
    label: list.user_data,
    value: list.list_id,
  })) ?? [];

  const {
    handleSubmit, control, register, formState: { errors }, watch, setValue,
  } = useForm<TCreateCardFormData>({
    defaultValues: {
      fio: defaultValues?.fio ?? '',
      /**
       * Возможные варианты sampleId:
       * - undefined - файл не загружали, нет необходимости валидировать
       * - null - файл загрузили, но детекции не выбрали
       * - string - файл загрузили и выбрали сэмпл
       */
      sampleId: defaultValues?.sampleId ?? undefined,
      lists: defaultValues?.lists ?? undefined,
      attachments: defaultValues?.attachments ?? undefined,
    },
  });
  const selectedLists = watch('lists');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={st.Page}>
        <div className={st.ModalHeader}>
          <span>{t('features:createCard.title')}</span>
        </div>
        <div className={st.ModalBody}>
          {!defaultValues?.sampleId && (
            <CardFaceInput
              control={control}
              name="sampleId"
            />
          )}
          {!!defaultValues?.sampleId && (
            <FaceSample
              className={st.ExistedFaceSample}
              src={`/api/lp5/6/samples/faces/${defaultValues.sampleId}`}
            />
          )}
          <div className={st.DataContainer}>
            <div>
              <Input
                id="fio"
                label={t('features:createCard.form_fields.fio')}
                {...register('fio', {
                  required: validate.requiredValidator(),
                  maxLength: validate.maxLengthValidator(128),
                })}
                errors={errors}
              />
            </div>
            <div>
              <Select
                disabled={!!defaultValues?.lists || listOptions.length < 1}
                errors={errors}
                id="lists"
                isClearable
                isMulti
                label={t('features:createCard.form_fields.lists')}
                name="lists"
                onBlur={register('lists').onBlur}
                onChange={(newValue: TOption[]) => {
                  setValue('lists', newValue.map(({ value }) => value));
                }}
                options={listOptions}
                value={selectedLists
                  ?.map((listId) => listOptions.find((listOption) => listOption.value === listId))
                  .filter((l) => l !== undefined) ?? []}
              />
            </div>
            <div className={st.AttachmentContainer}>
              <AttachmentsInput
                control={control}
                name="attachments"
              />
            </div>
          </div>
        </div>
        <div className={st.ModalFooter}>
          <div className={st.ButtonWrapper}>
            <Button
              data-testid="submitCardButton"
              fullWidth
              type="submit"
            >
              {t('features:createCard.btn.create')}
            </Button>
          </div>
          <div className={st.ButtonWrapper}>
            <Button
              data-testid="closeCardButton"
              fullWidth
              kind="secondary"
              onClick={onCancel}
              variant="outlined"
            >
              {t('features:createCard.btn.cancel')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
